import { useEffect, useRef, useState } from 'react';
import { Divider } from '../../ui/Divider/Divider';
import { Input } from '../../ui/Input/Input';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { AccountSave } from './AccountSave';
import {
  setName as saveName,
  setLastName as saveLastName,
  setPersonalInfoChanged
} from '../../../store/slices/accountSlice';
import { Toast } from '../../ui/Toast/Toast';
import ModalChangePassword from '../../ui/Modal/ModalChangePassword';
import ModalDeleteAccount from '../../ui/Modal/ModalDeleteAccount';
import Loader from '../../ui/Loader/Loader';
import { getBillingPortal } from '../../../services/BillingService';
import {
  getCurrentUser,
  updateCurrentUser
} from '../../../services/UserService';
import { TextButton } from '../../ui/Button/Buttons';
import { useAccountDefault } from '../../hooks/useAccountDefault';
import { featureFlags } from '../../../config/featureFlags';
import { selectAuthUser } from '../../../store/selectors/authSelectors';
import { getPlatformRoleLabel } from '../../../utils/Functions.utils';

const NAME_REGEX = /^[A-Za-zÀ-ÖØ-öø-ÿ\s'-]+$/;

type NameErrors = {
  firstName: string;
  lastName: string;
};

const validateName = (value: string, label: 'First name' | 'Last name') => {
  if (!value.trim()) return `${label} is required.`;
  if (!NAME_REGEX.test(value)) {
    return `${label} can only contain letters, spaces, apostrophes, and hyphens.`;
  }
  return '';
};

export default function Account() {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.account);

  const userRole = useSelector(selectAuthUser)?.role;
  const isSuperAdminRole = userRole === 'SUPER_ADMIN';

  const { account, accountLoading } = useSelector(
    (state: RootState) => state.accountDefault
  );
  const { fetchMyAccount } = useAccountDefault();
  const hasFetched = useRef(false);
  const hasRequestedAccount = useRef(false);

  const hasNoPlan = account?.subscription?.plan === 'NO_PLAN';

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [loading, setLoading] = useState(true);
  const [billingUrl, setBillingUrl] = useState('');
  const [buttonText, setButtonText] = useState('Save Changes');
  const [errors, setErrors] = useState<NameErrors>({
    firstName: '',
    lastName: ''
  });
  const [organization, setOrganization] = useState<string | undefined>('');

  const fetchData = async () => {
    hasFetched.current = true;
    setLoading(true);

    try {
      const [billingUrl, currentUser] = await Promise.all([
        getBillingPortal(),
        getCurrentUser()
      ]);

      setBillingUrl(billingUrl.data.url);
      setEmail(currentUser.data.email!);
      setFirstName(currentUser.data.firstName);
      dispatch(saveName(currentUser.data.firstName));
      setLastName(currentUser.data.lastName);
      dispatch(saveLastName(currentUser.data.lastName));
      setOrganization(currentUser.data.company);
    } catch (error) {
      console.error('Error fetching data: ', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const nextErrors = {
      firstName: validateName(firstName, 'First name'),
      lastName: validateName(lastName, 'Last name')
    };

    setErrors(nextErrors);

    if (nextErrors.firstName || nextErrors.lastName) return;

    const requestBody = {
      firstName,
      lastName
    };

    try {
      setButtonText('Saving...');
      await updateCurrentUser(requestBody);

      dispatch(saveName(firstName));
      dispatch(saveLastName(lastName));

      setShowToast(true);
    } catch (error) {
      console.error('Failed to save changes: ', error);
    } finally {
      setButtonText('Save Changes');
    }
  };

  const handleCancel = () => {
    setFirstName(user.firstName);
    setLastName(user.lastName);
    setErrors({ firstName: '', lastName: '' });
  };

  const handleFirstNameChange = (value: string) => {
    setFirstName(value);
    if (errors.firstName) {
      setErrors((currentErrors) => ({
        ...currentErrors,
        firstName: validateName(value, 'First name')
      }));
    }
  };

  const handleLastNameChange = (value: string) => {
    setLastName(value);
    if (errors.lastName) {
      setErrors((currentErrors) => ({
        ...currentErrors,
        lastName: validateName(value, 'Last name')
      }));
    }
  };

  const sectionHeader = (title: string, text: string) => {
    return (
      <span className="flex flex-col gap-1 items-start">
        <h2 className="text-zinc-900 font-medium">{title}</h2>
        <p className="text-sm text-gray-500">{text}</p>
      </span>
    );
  };

  useEffect(() => {
    if (account || accountLoading || hasRequestedAccount.current) {
      return;
    }

    hasRequestedAccount.current = true;
    fetchMyAccount();
  }, [account, accountLoading, fetchMyAccount]);

  useEffect(() => {
    // prevent making api calls twice
    if (hasFetched.current) return;

    fetchData();
  }, []);

  useEffect(() => {
    if (loading) return;

    dispatch(
      setPersonalInfoChanged(
        firstName !== user.firstName || lastName !== user.lastName
      )
    );
  }, [firstName, lastName, user.firstName, user.lastName, loading]);

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm flex-1 max-w-none w-full flex flex-col p-4 h-[calc(100vh_-_212px)]">
      {loading ? (
        <div className="absolute top-1/2 left-1/2">
          <Loader />
        </div>
      ) : (
        <div className="overflow-y-auto">
          <div className="flex flex-col gap-5 px-4 py-2">
            {/* Personal information */}
            <div className="flex flex-col gap-6">
              <span className="flex flex-col gap-1 items-start">
                <h2 className="text-zinc-900 font-medium">
                  Personal information
                </h2>
                <p className="text-sm text-gray-500">
                  Manage your basic details.
                </p>
              </span>
              <div className="flex gap-6">
                <div className="flex flex-col gap-6 w-1/2">
                  <Input
                    label="First name"
                    id="name"
                    value={firstName}
                    maxLength={100}
                    onChange={(e) => handleFirstNameChange(e.target.value)}
                    onBlur={() =>
                      setErrors((currentErrors) => ({
                        ...currentErrors,
                        firstName: validateName(firstName, 'First name')
                      }))
                    }
                    error={errors.firstName}
                    containerClassName="flex flex-col items-start gap-1 self-stretch"
                    labelClassName="text-sm text-gray-800 font-medium"
                    inputClassName="input"
                  />
                  <Input
                    label="Email address"
                    id="email"
                    value={email}
                    maxLength={100}
                    onChange={() => {}}
                    disabled
                    containerClassName="flex flex-col items-start gap-1 self-stretch"
                    labelClassName="text-sm text-gray-800 font-medium"
                    inputClassName="w-full px-[13px] py-[9px] bg-gray-100 ring-1 ring-gray-200 rounded-md text-gray-900 shadow-sm"
                  />
                </div>
                <div className="w-1/2">
                  <Input
                    label="Last name"
                    id="lastName"
                    value={lastName}
                    maxLength={100}
                    onChange={(e) => handleLastNameChange(e.target.value)}
                    onBlur={() =>
                      setErrors((currentErrors) => ({
                        ...currentErrors,
                        lastName: validateName(lastName, 'Last name')
                      }))
                    }
                    error={errors.lastName}
                    containerClassName="flex flex-col items-start gap-1 self-stretch"
                    labelClassName="text-sm text-gray-800 font-medium"
                    inputClassName="input"
                  />
                </div>
              </div>
            </div>

            <Divider classes="my-3.5" />

            {/* Organization */}
            <div className="flex flex-col gap-4">
              {sectionHeader(
                'Organization',
                'View your organization and access role.'
              )}
              <div className="flex gap-6">
                <div className="w-1/2">
                  <Input
                    id="organization"
                    label="Organization"
                    maxLength={255}
                    onChange={() => {}}
                    value={organization || ''}
                    containerClassName="flex flex-col items-start gap-1 self-stretch"
                    labelClassName="text-sm text-gray-800 font-medium"
                    inputClassName="w-full px-[13px] py-[9px] bg-gray-100 ring-1 ring-gray-200 rounded-md text-gray-900 shadow-sm"
                    disabled
                  />
                </div>
                <div className="w-1/2">
                  <Input
                    id="role"
                    label="Role"
                    maxLength={255}
                    onChange={() => {}}
                    value={getPlatformRoleLabel(userRole)}
                    containerClassName="flex flex-col items-start gap-1 self-stretch"
                    labelClassName="text-sm text-gray-800 font-medium"
                    inputClassName="w-full px-[13px] py-[9px] bg-gray-100 ring-1 ring-gray-200 rounded-md text-gray-900 shadow-sm"
                    disabled
                  />
                </div>
              </div>
            </div>

            <Divider classes="my-3.5" />

            {/* Security */}
            <div className="flex flex-col gap-4">
              {sectionHeader(
                'Security',
                'Manage your password and keep your account secure.'
              )}
              <ModalChangePassword setShowToast={setShowToast} />
            </div>

            {/* Billing & Subscription - hide for now 

            <Divider classes="my-3.5" />
            
            {featureFlags.billingAndSubscriptionEnabled && (
              <>
                <div className="flex flex-col items-start gap-4">
                  {sectionHeader(
                    'Billing & Subscription',
                    'Manage your billing information and subscription plan.'
                  )}
                  {hasNoPlan ? (
                    <TextButton disabled>
                      Manage Billing & Subscription
                    </TextButton>
                  ) : (
                    <a href={billingUrl} target="blank">
                      <TextButton>Manage Billing & Subscription</TextButton>
                    </a>
                  )}
                </div>
              </>
            )} */}

            {/* Delete Account */}
            {!isSuperAdminRole && (
              <>
                <Divider classes="my-3" />

                <div className="flex flex-col gap-4 mb-4">
                  {sectionHeader(
                    'Account',
                    'Permanently delete your account and all data.'
                  )}
                  <ModalDeleteAccount />
                </div>
              </>
            )}

            {showToast && (
              <Toast showToast={showToast} setShowToast={setShowToast} />
            )}
          </div>
        </div>
      )}
      {user.personalInfoChanged && !loading && (
        <AccountSave
          handleSave={handleSave}
          handleCancel={handleCancel}
          disableSave={
            firstName.length === 0 ||
            lastName.length === 0 ||
            Boolean(errors.firstName) ||
            Boolean(errors.lastName) ||
            buttonText === 'Saving...'
          }
          buttonText={buttonText}
        />
      )}
    </div>
  );
}
